import { useEffect, useMemo, useState } from 'react'
import { AlertCircle, Download, FileText, Loader2, Tag } from 'lucide-react'
import { useApp } from '../App'
import { airtableAPI, menusAPI } from '../lib/api'
import {
  buildCustomerLabelGroups,
  createCustomerLabelsPdf,
  customerLabelsFileName,
} from '../lib/customerLabels'
import dmSansBoldUrl from '../assets/DMSans-Bold.ttf?url'

let fontBytesPromise

function loadFontBytes() {
  if (!fontBytesPromise) {
    fontBytesPromise = fetch(dmSansBoldUrl).then((response) => {
      if (!response.ok) throw new Error(`Could not load DM Sans Bold (${response.status})`)
      return response.arrayBuffer()
    })
  }
  return fontBytesPromise
}

function downloadPdf(bytes, fileName) {
  const blob = new Blob([bytes], { type: 'application/pdf' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), 1000)
}

function previewNameSize(name) {
  if (name.length > 30) return 'text-base'
  if (name.length > 22) return 'text-lg'
  return 'text-2xl'
}

export default function CustomerLabels() {
  const { currentCity, currentMenu, menus, cities } = useApp()
  const [menu, setMenu] = useState(null)
  const [latest, setLatest] = useState(null)
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!currentCity || !currentMenu) {
      setMenu(null)
      setLatest(null)
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    Promise.all([
      menusAPI.get(currentMenu),
      airtableAPI.getLatest(currentCity, currentMenu),
    ])
      .then(([menuData, importData]) => {
        if (cancelled) return
        setMenu(menuData)
        setLatest(importData)
      })
      .catch((err) => { if (!cancelled) setError(err.message) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [currentCity, currentMenu])

  const labelData = useMemo(
    () => buildCustomerLabelGroups(menu, latest?.customizations),
    [menu, latest]
  )
  const customerCount = useMemo(
    () => new Set(labelData.labels.map((label) => label.name)).size,
    [labelData.labels]
  )
  const menuName = menus.find((entry) => entry.id === currentMenu)?.name || menu?.name || 'Current menu'
  const cityName = cities.find((city) => city.id === currentCity)?.name || currentCity?.toUpperCase()

  async function generate(labels, fileName, title, key) {
    if (!labels.length || generating) return
    setGenerating(key)
    setError(null)
    try {
      const fontBytes = await loadFontBytes()
      const pdfBytes = await createCustomerLabelsPdf(labels, fontBytes, title)
      downloadPdf(pdfBytes, fileName)
    } catch (err) {
      setError(`Could not create labels: ${err.message}`)
    } finally {
      setGenerating(null)
    }
  }

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="w-7 h-7 text-havn-green-700 animate-spin" /></div>

  return (
    <div className="max-w-6xl mx-auto animate-fade-in pb-12">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.16em] text-havn-brass-600 font-medium">Print center</p>
          <h1 className="font-display text-3xl text-havn-green-900 mt-1">Customer labels</h1>
          <p className="text-sm text-havn-gray-500 mt-1">{menuName} · {cityName}</p>
          <p className="text-sm text-havn-gray-600 mt-3 max-w-3xl">
            Real 2 × 1-inch PDFs with embedded DM Sans Bold—one page per custom meal. Labels use the exact names and quantities from the printable Customs plate cards; Needs review and Side notes are excluded.
          </p>
        </div>
        <button
          disabled={!labelData.totalLabels || !!generating}
          onClick={() => generate(
            labelData.labels,
            'Customer-Labels_All-Dishes.pdf',
            `${menuName} — All Customer Labels`,
            'all'
          )}
          className="btn-primary min-h-[48px] px-5 inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {generating === 'all' ? <Loader2 className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" />}
          Download all {labelData.totalLabels} labels
        </button>
      </div>

      <div className="grid sm:grid-cols-3 gap-3 mb-6">
        <div className="card p-4"><Tag className="w-5 h-5 text-havn-green-700" /><strong className="font-display text-3xl text-havn-green-900 block mt-2 tabular-nums">{labelData.totalLabels}</strong><span className="text-sm text-havn-gray-500">printable meal labels</span></div>
        <div className="card p-4"><strong className="font-display text-3xl text-havn-green-900 block mt-7 tabular-nums">{customerCount}</strong><span className="text-sm text-havn-gray-500">customers on plate cards</span></div>
        <div className="card p-4"><strong className="font-display text-2xl text-havn-green-900 block mt-7">144 × 72 pt</strong><span className="text-sm text-havn-gray-500">exact 2 × 1-inch PDF pages</span></div>
      </div>

      {error && <div className="rounded-xl border border-red-200 bg-red-50 text-red-700 px-4 py-3 mb-5">{error}</div>}

      {labelData.missingNameMeals > 0 && (
        <div role="status" className="rounded-xl border border-amber-200 bg-amber-50 text-amber-900 px-4 py-3 mb-5">
          {labelData.missingNameMeals} custom-meal labels need customer names. Add the names in Havn OS and re-import to include them.
        </div>
      )}

      {!labelData.totalLabels && !error ? (
        <div className="card p-10 text-center"><AlertCircle className="w-8 h-8 text-havn-gray-400 mx-auto mb-3" /><h2 className="font-display text-xl text-havn-green-900">No customer labels</h2><p className="text-sm text-havn-gray-500 mt-1">The latest import has no printable custom meal cards for this menu.</p></div>
      ) : (
        <div className="space-y-5">
          {labelData.groups.map((group, groupIndex) => (
            <section key={group.dish.dish_id} className="card p-4 lg:p-5">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-full bg-havn-green-900 text-white text-sm font-bold flex items-center justify-center tabular-nums">{groupIndex + 1}</span>
                  <div>
                    <h2 className="font-display text-xl text-havn-green-900">{group.dish.name}</h2>
                    <p className="text-xs text-havn-gray-500">{group.labels.length} labels · matches the Customs sheet quantity</p>
                  </div>
                </div>
                <button
                  disabled={!!generating}
                  onClick={() => generate(
                    group.labels,
                    customerLabelsFileName(group, groupIndex),
                    `${menuName} — ${group.dish.name} Customer Labels`,
                    group.dish.dish_id
                  )}
                  className="btn-secondary min-h-[44px] px-4 inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {generating === group.dish.dish_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                  Download {group.labels.length}-page PDF
                </button>
              </div>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {group.labels.map((label, labelIndex) => (
                  <div key={`${group.dish.dish_id}-${labelIndex}-${label.name}`} className="aspect-[2/1] bg-white border border-havn-cream-300 rounded-lg flex items-center justify-center px-[5%] py-[3%] text-center overflow-hidden">
                    <strong className={`${previewNameSize(label.name)} leading-[1.05] tracking-[-0.01em] font-bold text-black break-normal [overflow-wrap:normal]`}>{label.name}</strong>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  )
}
